import { useState, useContext } from 'react';
import { useSelector } from 'react-redux';

import DeleteIcon from '@mui/icons-material/Delete';
import LinkIcon from '@mui/icons-material/Link';

import ResourceModal from 'app/components/ResourceModal';
import ResourceModalPanel from 'app/components/ResourceModalPanel';
import DefaultMomentsUploadModal from 'app/components/Uploads/DefaultMomentsUploadModal';
import useMomentsUploadForm from 'app/hooks/useMomentsUploadForm';
import useDeeplink from 'app/hooks/useDeeplink';
import CatalogForm from './CatalogForm';
import UploadModal from 'app/components/Uploads/UploadModal';
import UploadPreview from 'app/components/PhotoSwipe/UploadPreview';
import UploadWorker from 'app/workers/UploadWorker';
import JobUploadProgressBar from 'app/components/Jobs/JobUploadProgressBar';

import {
  AND_OPERATOR,
} from 'app/hooks/useFilter';

import {
  deleteDefaultMoment
} from 'app/services/jobs';

import {
  getMoments
} from 'app/services/moments';

import {
  moments
} from 'app/resources';

const CatalogPanel = props => {
  const {
    job,
    action,
    onDelete,
    onUploadStart,
    onUploadComplete,
    updateRow
  } = props;

  const uploadWorker = useContext(UploadWorker);
  const account = useSelector(state => state.portalReducer.account);

  const showMomentUpload = !account.feature_flags.disable_upload;

  const {
    formIsOpen: defaultMomentsFormIsOpen,
    onAdd: uploadDefaultMoments,
    onFormClose: onDefaultMomentsFormClose,
    onFormSubmit: onDefaultMomentsFormSubmit
  } = useMomentsUploadForm({
    onUploadStart,
    onUploadComplete,
    job
  });

  const deeplink = useDeeplink();

  // if we're creating a job then no actions panel will be shown
  if (action === 'create') {
    return null;
  }

  // get images for Default Moments card
  const getDefaultImages = async (page = 1, perPage = 100) => {
    let filterModel = {
      story_id: [{
        filterClass: 'text',
        filterType: 'equals',
        value: job.template_story_id.toString()
      }],
    }

    let params = {
      page,
      per_page: perPage,
      filter_model: filterModel,
      sort_model: []
    }

    let resp = await getMoments(params);

    let images = resp.records.map(moment => {
      return {
        id: moment.id,
        src: moment.preview_url,
        w: moment.width,
        h: moment.height,
        filename: moment.filename,
        original: moment.print_url,
        is_video: moment.type && moment.type.includes("video")
      }
    });

    return images;
  }

  const deleteMoment = async (moment) => {
    return await deleteDefaultMoment(job.id, moment.id);
  }

  const viewMoment = (moment, newTab = false) => {
    let filterModel = {
      id: [{
        filterClass: 'text',
        filterType: 'equals',
        value: moment.id.toString(),
        operator: AND_OPERATOR
      }]
    }

    if (newTab) {
      // return link to set as anchor href with target="_blank"
      return deeplink.getLink(moments, filterModel);
    } else {
      deeplink.load(moments, filterModel);
    }
  }

  const viewDefaultMoments = (newTab = false) => {
    let filterModel = {
      job_id: [{
        filterClass: 'text',
        filterType: 'equals',
        value: job.id.toString(),
        operator: AND_OPERATOR
      }],
      is_default: [{
        filterClass: 'text',
        filterType: 'equals',
        value: '1',
        operator: AND_OPERATOR
      }]
    }

    if (newTab) {
      // return link to set as anchor href with target="_blank"
      return deeplink.getLink(moments, filterModel);
    } else {
      deeplink.load(moments, filterModel);
    }
  }

  let defaultMomentActions = [];
  if (showMomentUpload) {
    defaultMomentActions.push(
      { label: 'Upload Moments', callback: uploadDefaultMoments }
    );
  }
  defaultMomentActions.push(
    { label: 'View Moments', callback: viewDefaultMoments, newTabLink: viewDefaultMoments(true) }
  );

  let cards = [
    {
      label: 'Catalog Moments',
      getImages: getDefaultImages,
      imageCount: job.expected_photo_count,
      imagesPerPage: 100,
      progressBar: <JobUploadProgressBar job={job} inModal={true} />,
      details: [
        { label: 'Catalog Moments', value: job.actual_photo_count }
      ],
      footnote: 'Catalog moments will appear in the kiosk for every customer',
      actions: defaultMomentActions,
      psActions: [
        { key: "delete", icon: DeleteIcon, onClick: deleteMoment },
        { key: "link", icon: LinkIcon, onClick: viewMoment },
      ]
    },
    {
      label: 'Delete Catalog',
      message: 'Deleting a catalog will permanently remove its images.',
      actions: [
        { label: 'Delete Catalog', callback: onDelete }
      ]
    }
  ]

  return (
    <div>
      <ResourceModalPanel cards={cards} />
      <DefaultMomentsUploadModal
        isOpen={defaultMomentsFormIsOpen}
        onClose={onDefaultMomentsFormClose}
        onFormSubmit={onDefaultMomentsFormSubmit}
        job={job}
        center={true}
      />
    </div>
  );
}

const CatalogModal = props => {
  const {
    isOpen,
    onClose,
    onFormSubmit,
    onDestroy,
    job,
    kiosks,
    onUploadStart,
    onUploadComplete,
    updateRow,
    jobConfigs
  } = props;

  const action = job ? 'update' : 'create';

  let title, subTitle = null;
  if (action === 'update') {
    title = job.name;
    subTitle = job.event_id;
  } else {
    title = 'New Catalog';
  }

  const onSubmit = params => onFormSubmit(action, params)
  const onDelete = () => onDestroy(job.id);

  return (
    <ResourceModal {...{ isOpen, onClose, title, subTitle }}>
      <CatalogPanel {...{ job, action, onClose, onDelete, onUploadStart, onUploadComplete, updateRow }} />
      <CatalogForm {...{ job, action, onClose, onSubmit, kiosks, jobConfigs }} />
    </ResourceModal>
  );
}

export default CatalogModal;
